// updateAllocation gets the wrong initialReferenceAllocationId OR referenceAllocationId for ExternalEvents, because
// after the create, there is an additional updated on variant.referenceAllocationId (first allocation becomes)
// the reference allcation
// that seems to be not respected by the Allocation.state machine
// perhaps we should call update, updateAndDuplicate, duplicate directly from Allocation.state and must not
// pass initial/newValue to distinguish between the 3 states?? or fix the non updated refId

// perhaps there is a missing await in Allocation.state for onCreateAllocation with the onDone stuff?

import {
    AllocationPhaseDeleteInput,
    AllocationPhaseInput,
    AllocationPhaseUpdateInput,
    AllocationPhaseUpdateInputFlat,
    TimelineAllocationResponse,
    CopyTargetLocation,
} from '@/components/Timeline/allocation/types';
import { useCreateAllocationUpdateRequestPhases } from '@/components/Timeline/composables/useCreateAllocationUpdateRequestPhases';
import { calculatePhaseUpdate } from '@/components/Timeline/logic/durations';
import { validateAllocationUpdateResponse } from '@/components/Timeline/timeline';
import { AllocationPhaseUpdate, LocationTypeCategory } from '@/components/Timeline/Timeline.types';
import { DateTime } from 'luxon';

export type UpdateAllocationReturn = {
    phaseUpdates: AllocationPhaseUpdate[];
    isOptional: boolean;
    referenceAllocationId: string | null;
};

export function useUpdateAllocations(
    updateAllocationData: (
        variantId: string,
        allocationId: string,
        locationTypeCategory: LocationTypeCategory,
        start: DateTime,
        end: DateTime,
        isOptional: boolean,
        createPhases: AllocationPhaseInput[],
        updatePhases: AllocationPhaseUpdateInput[],
        deletePhases: AllocationPhaseDeleteInput[],
        remainingPhases: (AllocationPhaseUpdate & { dayOffsetCount: number })[],
        referenceAllocationId?: string | null,
    ) => Promise<UpdateAllocationReturn>,
    duplicateAllocation: (
        variantId: string,
        allocationId: string,
        newLocations: CopyTargetLocation[],
        locationTypeCategory: LocationTypeCategory,
    ) => Promise<void>,
    duplicateAndUpdateAllocation: (
        variantId: string,
        allocationId: string,
        newLocations: CopyTargetLocation[],
        locationTypeCategory: LocationTypeCategory,
        start: DateTime,
        end: DateTime,
        isOptional: boolean,
        createPhases: AllocationPhaseInput[],
        updatePhases: AllocationPhaseUpdateInputFlat[],
        deletePhases: AllocationPhaseDeleteInput[],
        referenceAllocationId?: string | null,
    ) => Promise<TimelineAllocationResponse | undefined>,
) {
    async function updateAllocation(
        phases: AllocationPhaseUpdate[],
        initialStartDateTime: DateTime,
        initialEndDateTime: DateTime,
        newLocations: CopyTargetLocation[],
        allocationId: string,
        locationTypeCategory: LocationTypeCategory,
        allocationVariantId: string,
        initialIsOptional: boolean,
        isAllocationOptional: boolean,
        initialReferenceAllocationId: string | null,
        referenceAllocationId: string | null,
    ): Promise<UpdateAllocationReturn> {
        try {
            const allocation = {
                startDate: initialStartDateTime,
                endDate: initialEndDateTime,
            };

            const { startDateTime, endDateTime } = calculatePhaseUpdate(phases, allocation);

            /* There are 3 possible cases for phase updates:
             * 1) Update only phases, without duplicating the allocation into other locations
             * 2) Only duplicate the allocation into other locations, without updating the phases
             * 3) Update phases + duplicate the allocation into other locations
             */

            const isDuplicated = newLocations.length > 0;
            const hasPhaseChanges = phases.some((phase) => phase.cellSpanBefore !== 0 || phase.cellSpanAfter !== 0);
            const hasSpecializationChanges = phases.some((phase) => phase.newSpecialization !== phase.specialization);
            const hasIsOptionalChange = initialIsOptional !== isAllocationOptional;
            const hasReferenceAllocationIdChange = initialReferenceAllocationId !== referenceAllocationId;

            const hasChanges =
                hasPhaseChanges || hasSpecializationChanges || hasIsOptionalChange || hasReferenceAllocationIdChange;

            const { createRequestPhases } = useCreateAllocationUpdateRequestPhases(phases);
            const { phasesToCreate, updatePhases, phasesToUpdate, phasesToDelete, remainingPhases } =
                createRequestPhases();

            // Case 1 (only phase update or isOptionalChange or reference allocation update)
            if (!isDuplicated && hasChanges) {
                const result = await updateAllocationData(
                    allocationVariantId,
                    allocationId,
                    locationTypeCategory,
                    startDateTime,
                    endDateTime,
                    isAllocationOptional,
                    phasesToCreate,
                    phasesToUpdate,
                    phasesToDelete,
                    remainingPhases,
                    hasReferenceAllocationIdChange ? referenceAllocationId : undefined,
                );
                // updateAllocationData reports referenceAllocationId as null when it was not part of
                // the update, so the confirmed value is always the one the popup was closed with
                return { ...result, referenceAllocationId };
            }

            // Case 2 (Duplicated, no phase changes or isOptional change or referenceAllocationId change)
            if (isDuplicated && !hasChanges) {
                await duplicateAllocation(allocationVariantId, allocationId, newLocations, locationTypeCategory);
                return { phaseUpdates: updatePhases, isOptional: isAllocationOptional, referenceAllocationId };
            }

            // Case 3 (Duplicated + phase changes)
            if (isDuplicated && hasChanges) {
                const updatedAllocation = await duplicateAndUpdateAllocation(
                    allocationVariantId,
                    allocationId,
                    newLocations,
                    locationTypeCategory,
                    startDateTime,
                    endDateTime,
                    isAllocationOptional,
                    phasesToCreate,
                    phasesToUpdate.map((p) => ({
                        id: p.id,
                        duration: p.patch.duration,
                        offset: p.patch.offset,
                        specializationType: p.patch.specializationType,
                        usageId: p.patch.usageId,
                    })),
                    phasesToDelete,
                    hasReferenceAllocationIdChange ? referenceAllocationId : undefined,
                );
                const confirmedPhases = validateAllocationUpdateResponse(updatedAllocation, remainingPhases);
                return { phaseUpdates: confirmedPhases, isOptional: isAllocationOptional, referenceAllocationId };
            }

            // Case 4 (Not duplicated + no phase changes) .. this should not happen
            if (!isDuplicated && !hasChanges) {
                throw Error('No changes detected for update and/or duplicate Allocation');
            }
        } catch (error: unknown) {
            throw Error(`Error on update and/or duplicating Allocation ${error}`);
        }
        throw Error('Error on update and/or duplicating Allocation');
    }

    return {
        updateAllocation,
    };
}
